/*
 * <sk-campaign> — native storefront filters/sorting on a metaobject (Campanha) page.
 *
 * Storefront facets only work on collection/search routes, so this controller
 * proxies the campaign's collection on connect and whenever facets change. The
 * server-rendered grid is a no-JS fallback; facet markup is always injected from
 * the hidden collection template. The visible URL stays the campaign metaobject URL.
 *
 * Markup contract (see sections/sk-campaign.liquid):
 *   <sk-campaign
 *     data-filters-enabled="true"
 *     data-collection-url="/collections/<handle>"   {# section.settings.collection.url #}
 *     data-proxy-section-id="main">                  {# section key in collection.campanha.json #}
 *     <div ref="results"> … server-rendered fallback grid … </div>
 *   </sk-campaign>
 *
 * Horizon's own facet JS (facets.js / results-list) always re-renders against
 * `window.location`, so it can't drive this off-route. We reuse Horizon's facet
 * *markup* (correct counts, swatches, price bounds) but own the fetch/inject and
 * neutralize Horizon's filtering handlers (which are attribute-driven, see component.js).
 *
 * Drawer UX: filter and sort inputs inside `#filters-drawer` are draft-only — the
 * proxy request runs when the user clicks "Aplicar" (`.facets__see-results`).
 * Remove-filter chips (`data-url`) still apply immediately. Collection-path links
 * in the proxied grid (e.g. empty-state "clear all filters") are rewritten and
 * intercepted so the user stays on the campaign URL.
 */

import { Component } from '@theme/component';
import { DialogComponent } from '@theme/dialog';
import { sectionRenderer } from '@theme/section-renderer';

/** Horizon `on:` handler names that mutate results — we own these instead. */
const FILTER_METHOD_RE = /updateFilters|updateFilterAndSorting|removeFilter|clearFilter/;

/** Containers that mark a click/change as "inside the facets UI" (vs a product link). */
const FACETS_SCOPE = '.facets, .facets-block-wrapper, .facets-controls-wrapper, facet-remove-component';

/**
 * @typedef {object} Refs
 * @property {HTMLElement} results - Container that holds the facets + product grid.
 *
 * @extends {Component<Refs>}
 */
export class SkCampaign extends Component {
  requiredRefs = ['results'];

  /** @type {IntersectionObserver | undefined} */
  #observer;
  /** @type {URLSearchParams} */
  #params = new URLSearchParams();
  /**
   * Fully-qualified section id of the collection grid (e.g. `template--123__main`).
   * The Section Rendering API requires this — the bare JSON key (`main`) 404s — so we
   * discover it from the collection page on the first fetch and reuse it afterwards.
   * @type {string | null}
   */
  #resolvedSectionId = null;
  #currentPage = 1;
  #lastPage = 1;
  #loading = false;

  connectedCallback() {
    super.connectedCallback();

    if (this.dataset.filtersEnabled !== 'true' || !this.collectionUrl) return;

    this.#params = this.#readParamsFromLocation();

    this.addEventListener('change', this.#onChange);
    this.addEventListener('click', this.#onClick, true);

    // Facets are empty on metaobject routes — always proxy the collection section so
    // filters/sort render on first paint (URL params are honoured via #params).
    this.#render();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('change', this.#onChange);
    this.removeEventListener('click', this.#onClick, true);
    this.#observer?.disconnect();
  }

  get collectionUrl() {
    return this.dataset.collectionUrl || '';
  }

  get grid() {
    const grid = this.refs.results?.querySelector('[ref="grid"]');
    return grid instanceof HTMLElement ? grid : null;
  }

  /** Seed filter/sort state from the current URL so refresh/share keeps it. */
  #readParamsFromLocation() {
    const params = new URLSearchParams();
    for (const [key, value] of new URL(window.location.href).searchParams) {
      if (key.startsWith('filter.') || key === 'sort_by') params.append(key, value);
    }
    return params;
  }

  /**
   * @param {Record<string, string>} [extra]
   * @returns {URL}
   */
  #buildUrl(extra = {}) {
    const url = new URL(this.collectionUrl, window.location.origin);
    url.search = '';
    for (const [key, value] of this.#params) url.searchParams.append(key, value);
    for (const [key, value] of Object.entries(extra)) url.searchParams.set(key, value);
    return url;
  }

  /**
   * @param {string} html
   * @returns {Document}
   */
  #parse(html) {
    return new DOMParser().parseFromString(html, 'text/html');
  }

  /**
   * Fetches the collection markup for the given URL. Until the qualified section id is
   * known, fetches the full collection page and resolves it from the grid wrapper;
   * afterwards uses the (lighter) Section Rendering API.
   * @param {URL} url
   * @returns {Promise<string>}
   */
  async #fetchHTML(url) {
    if (this.#resolvedSectionId) {
      return sectionRenderer.getSectionHTML(this.#resolvedSectionId, false, url);
    }

    const response = await fetch(url.toString());
    const html = await response.text();
    const section = this.#parse(html).querySelector('.collection-wrapper')?.closest('[id^="shopify-section-"]');
    if (section) this.#resolvedSectionId = section.id.replace(/^shopify-section-/, '');
    return html;
  }

  /** Reflect current filters in the (metaobject) URL — Shopify ignores them on this route. */
  #syncLocation() {
    const url = new URL(window.location.href);
    url.search = '';
    for (const [key, value] of this.#params) url.searchParams.append(key, value);
    history.replaceState({}, '', url.toString());
  }

  /**
   * Swap in proxied collection markup while keeping the `results-list.section`
   * shell — that element owns the page-width grid (`.section > * { grid-column: 2 }`).
   * @param {Element} wrapper
   */
  #swapCollectionWrapper(wrapper) {
    const host = this.refs.results.querySelector('results-list') ?? this.refs.results;
    const current = host.querySelector(':scope > .collection-wrapper');

    if (current) {
      current.replaceWith(wrapper);
      return;
    }

    host.appendChild(wrapper);
  }

  /**
   * Full re-render: fetch the proxied collection section and swap facets + grid in.
   * @param {{ closeDrawer?: boolean }} [options]
   */
  async #render(options = {}) {
    if (this.#loading) return;
    this.#loading = true;

    const { closeDrawer = false } = options;
    const drawerState = closeDrawer ? { wasOpen: false, scrollTop: 0 } : this.#captureDrawerState();

    this.#setFacetsLoading(true);

    try {
      const html = await this.#fetchHTML(this.#buildUrl());
      const wrapper = this.#parse(html).querySelector('.collection-wrapper');
      if (!wrapper) return;

      this.#swapCollectionWrapper(wrapper);
      this.#neutralize();
      this.#rewriteCampaignLinks();
      this.#syncFacetDisabledState();

      this.#currentPage = 1;
      this.#lastPage = Number(this.grid?.dataset.lastPage ?? '1') || 1;
      this.#observeViewMore();
      this.setAttribute('initialized', '');

      await this.#restoreDrawerState(drawerState);

      if (closeDrawer) {
        await customElements.whenDefined('dialog-component');
        const dialogComponent = this.refs.results.querySelector('#filters-drawer');
        if (dialogComponent instanceof DialogComponent) dialogComponent.closeDialog();
      }
    } catch (error) {
      console.error('[sk-campaign] failed to render filtered results', error);
    } finally {
      this.#loading = false;
      this.#setFacetsLoading(false);
    }
  }

  /**
   * @returns {{ wasOpen: boolean, scrollTop: number }}
   */
  #captureDrawerState() {
    const dialog = this.refs.results.querySelector('#filters-drawer dialog');
    const filters = this.refs.results.querySelector('.facets-drawer__filters');
    return {
      wasOpen: dialog instanceof HTMLDialogElement && dialog.open,
      scrollTop: filters instanceof HTMLElement ? filters.scrollTop : 0,
    };
  }

  /**
   * Re-open the filters drawer after a results swap if it was open before.
   * @param {{ wasOpen: boolean, scrollTop: number }} state
   */
  async #restoreDrawerState(state) {
    if (!state.wasOpen) return;

    await customElements.whenDefined('dialog-component');

    const dialogComponent = this.refs.results.querySelector('#filters-drawer');
    if (!(dialogComponent instanceof DialogComponent)) return;

    dialogComponent.showDialog({ instant: true });

    const filters = this.refs.results.querySelector('.facets-drawer__filters');
    if (filters instanceof HTMLElement) filters.scrollTop = state.scrollTop;
  }

  /** Strip Horizon's result-mutating `on:` handlers; keep drawer/show-more/toggles. */
  #neutralize() {
    const elements = this.refs.results.querySelectorAll('[on\\:change],[on\\:click],[on\\:keydown],[on\\:keyup]');
    for (const element of elements) {
      for (const name of ['on:change', 'on:click', 'on:keydown', 'on:keyup']) {
        const value = element.getAttribute(name);
        if (value && FILTER_METHOD_RE.test(value)) element.removeAttribute(name);
      }
    }
  }

  get #collectionPath() {
    return new URL(this.collectionUrl, window.location.origin).pathname;
  }

  /**
   * @param {string | null} raw
   * @returns {boolean}
   */
  #isCollectionFacetHref(raw) {
    if (!raw || raw.startsWith('#')) return false;

    try {
      return new URL(raw, window.location.origin).pathname === this.#collectionPath;
    } catch {
      return false;
    }
  }

  /**
   * @param {string | null} raw
   * @returns {URLSearchParams}
   */
  #paramsFromCollectionHref(raw) {
    const params = new URLSearchParams();
    if (!raw) return params;

    try {
      const incoming = new URL(raw, window.location.origin).searchParams;
      for (const [key, value] of incoming) {
        if (key.startsWith('filter.') || key === 'sort_by') params.append(key, value);
      }
    } catch {
      // ignore malformed hrefs
    }

    return params;
  }

  /** Point proxied collection URLs (e.g. empty-state "clear filters") at the campaign page. */
  #rewriteCampaignLinks() {
    for (const link of this.refs.results.querySelectorAll('a[href]')) {
      if (!(link instanceof HTMLAnchorElement)) continue;

      const href = link.getAttribute('href');
      if (!this.#isCollectionFacetHref(href)) continue;

      const campaignUrl = new URL(window.location.pathname, window.location.origin);
      for (const [key, value] of this.#paramsFromCollectionHref(href)) {
        campaignUrl.searchParams.append(key, value);
      }

      link.href = `${campaignUrl.pathname}${campaignUrl.search}`;
    }
  }

  /**
   * @param {URLSearchParams} params
   * @param {{ closeDrawer?: boolean }} [options]
   */
  #applyParams(params, options = {}) {
    this.#params = params;
    this.#syncLocation();
    return this.#render(options);
  }

  /** Commit the drawer form's draft filter/sort state and close the drawer. */
  async #applyFromDrawer() {
    if (this.#loading) return;

    const form = this.refs.results.querySelector('#filters-drawer form.facets__form');
    if (!(form instanceof HTMLFormElement)) return;

    const params = this.#paramsFromForm(form);
    const activeSort = this.#params.get('sort_by');
    if (activeSort && !params.has('sort_by')) params.set('sort_by', activeSort);

    await this.#applyParams(params, { closeDrawer: true });
  }

  /**
   * Disable facet controls while a proxy request is in flight.
   * Preserves Shopify's zero-count `disabled` state when loading ends.
   * @param {boolean} loading
   */
  #setFacetsLoading(loading) {
    this.toggleAttribute('data-loading', loading);

    const drawer = this.refs.results.querySelector('#filters-drawer');
    if (!drawer) return;

    drawer.toggleAttribute('aria-busy', loading);

    for (const element of drawer.querySelectorAll('input, select, button.facets__see-results')) {
      if (
        element instanceof HTMLInputElement ||
        element instanceof HTMLSelectElement ||
        element instanceof HTMLButtonElement
      ) {
        if (loading) {
          element.toggleAttribute('data-sk-was-disabled', element.disabled);
          element.toggleAttribute('data-sk-loading', true);
          element.disabled = true;
        } else {
          element.removeAttribute('data-sk-loading');
          element.disabled = element.hasAttribute('data-sk-was-disabled');
          element.removeAttribute('data-sk-was-disabled');
        }
      }
    }

    if (!loading) this.#syncFacetDisabledState();
  }

  /** Re-apply disabled state from facet markup (zero-count values). */
  #syncFacetDisabledState() {
    const drawer = this.refs.results.querySelector('#filters-drawer');
    if (!drawer) return;

    for (const input of drawer.querySelectorAll('input')) {
      if (!(input instanceof HTMLInputElement) || input.hasAttribute('data-sk-loading')) continue;

      if (
        input.closest(
          '.facets__inputs-list-item--disabled, .checkbox--disabled, .variant-option--swatches-disabled'
        )
      ) {
        input.disabled = true;
      }
    }
  }

  /**
   * @param {Element} element
   * @returns {boolean}
   */
  #isFacetControl(element) {
    return Boolean(
      element.closest(
        '.facets, .facets-block-wrapper, .facets-controls-wrapper, #filters-drawer, facet-remove-component, facets-form-component'
      )
    );
  }

  /**
   * @param {HTMLInputElement | HTMLSelectElement} input
   * @returns {boolean}
   */
  #isFacetInputDisabled(input) {
    if (input.disabled) return true;

    return Boolean(
      input.closest(
        '.facets__inputs-list-item--disabled, .checkbox--disabled, .variant-option--swatches-disabled'
      )
    );
  }

  /**
   * @param {HTMLFormElement} form
   * @returns {URLSearchParams}
   */
  #paramsFromForm(form) {
    const params = new URLSearchParams();
    for (const [key, value] of new FormData(form)) {
      if (typeof value !== 'string' || value === '') continue;
      params.append(key, value);
    }
    params.delete('page');
    return params;
  }

  /** @returns {URLSearchParams} The active params limited to filter.* keys. */
  #filterOnlyParams() {
    const params = new URLSearchParams();
    for (const [key, value] of this.#params) {
      if (key.startsWith('filter.')) params.append(key, value);
    }
    return params;
  }

  /**
   * Select a sort option in the drawer without triggering a proxy request.
   * Horizon's SortingFilterComponent relies on updateFilterAndSorting (neutralized here);
   * hidden radios also need aria-selected kept in sync for the custom checkmarks.
   * @param {Element} option
   */
  #selectDrawerSortOption(option) {
    const input = option.querySelector('input[type="radio"][name="sort_by"]');
    if (!(input instanceof HTMLInputElement) || input.disabled) return;

    const listbox = option.closest('[role="listbox"]');
    if (listbox) {
      for (const opt of listbox.querySelectorAll('[role="option"]')) {
        opt.setAttribute('aria-selected', opt === option ? 'true' : 'false');
      }
    }

    input.checked = true;
  }

  /** @param {Event} event */
  #onChange = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement) && !(target instanceof HTMLSelectElement)) return;
    if (!this.refs.results.contains(target)) return;

    // Product card swatch pickers also use radio/checkbox inputs inside a <form>.
    if (!this.#isFacetControl(target)) return;

    // Drawer filters and sort are draft-only until the user clicks "Aplicar".
    if (target.closest('.facets--drawer, #filters-drawer')) {
      if (target instanceof HTMLInputElement && this.#isFacetInputDisabled(target)) return;
      return;
    }

    // Sorting controls can render outside the filter `<form>` on other layouts.
    if (target.name === 'sort_by') {
      event.stopImmediatePropagation();
      const params = this.#filterOnlyParams();
      if (target.value) params.set('sort_by', target.value);
      this.#applyParams(params);
      return;
    }

    const form = target.closest('form.facets__form');
    if (!(form instanceof HTMLFormElement)) return;

    event.stopImmediatePropagation();

    const params = this.#paramsFromForm(form);
    // Preserve the active sort when the filter form doesn't carry it.
    const activeSort = this.#params.get('sort_by');
    if (activeSort && !params.has('sort_by')) params.set('sort_by', activeSort);

    this.#applyParams(params);
  };

  /** @param {Event} event */
  #onClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const applyButton = target.closest('.facets__see-results');
    if (applyButton && this.refs.results.contains(applyButton)) {
      event.preventDefault();
      event.stopImmediatePropagation();
      this.#applyFromDrawer();
      return;
    }

    const sortOption = target.closest('.sorting-filter__option');
    if (
      sortOption &&
      sortOption.closest('#filters-drawer, .facets--drawer') &&
      this.refs.results.contains(sortOption)
    ) {
      event.preventDefault();
      event.stopImmediatePropagation();
      this.#selectDrawerSortOption(sortOption);
      return;
    }

    const disabledFacet = target.closest(
      '.facets__inputs-list-item--disabled, .checkbox--disabled, .variant-option--swatches-disabled'
    );
    if (
      disabledFacet &&
      disabledFacet.closest('#filters-drawer, .facets--drawer') &&
      this.refs.results.contains(disabledFacet)
    ) {
      event.preventDefault();
      event.stopImmediatePropagation();
      return;
    }

    const anchor = target.closest('a[href]');
    if (anchor && this.refs.results.contains(anchor) && this.#isCollectionFacetHref(anchor.getAttribute('href'))) {
      event.preventDefault();
      event.stopImmediatePropagation();
      this.#applyParams(this.#paramsFromCollectionHref(anchor.getAttribute('href')));
      return;
    }

    if (!target.closest(FACETS_SCOPE)) return;

    const link = target.closest('[data-url], a[href]');
    if (!link) return;

    const raw = link.getAttribute('data-url') ?? link.getAttribute('href');
    if (!raw) return;

    event.preventDefault();
    event.stopImmediatePropagation();

    this.#applyParams(this.#paramsFromCollectionHref(raw));
  };

  #observeViewMore() {
    this.#observer?.disconnect();

    const sentinel = this.refs.results.querySelector('[ref="viewMoreNext"]');
    if (!sentinel) return;

    this.#observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) this.#loadNextPage();
        }
      },
      { rootMargin: '300px' }
    );
    this.#observer.observe(sentinel);
  }

  async #loadNextPage() {
    if (this.#loading || this.#currentPage >= this.#lastPage) return;
    this.#loading = true;

    try {
      const next = this.#currentPage + 1;
      const html = await this.#fetchHTML(this.#buildUrl({ page: String(next) }));
      const cards = this.#parse(html).querySelectorAll('[ref="grid"] > [ref="cards[]"]');
      const grid = this.grid;

      if (grid && cards.length) {
        grid.append(...cards);
        this.#currentPage = next;
      }

      if (this.#currentPage >= this.#lastPage) this.#observer?.disconnect();
    } catch (error) {
      console.error('[sk-campaign] failed to load next page', error);
    } finally {
      this.#loading = false;
    }
  }
}

if (!customElements.get('sk-campaign')) {
  customElements.define('sk-campaign', SkCampaign);
}
